import { ApolloClient } from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import { SitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { CookiesProvider, RddkTheme, setGridConfiguration } from 'db-npm-e4sc-rd-ui';
import 'db-npm-e4sc-rd-ui/dist/fonts.css';
import 'db-npm-e4sc-rd-ui/dist/normalize.css';
import 'db-npm-e4sc-rd-ui/dist/styles.css';
import React from 'react';
import { Route, Routes, useParams } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import RouteHandler from './RouteHandler';
import { componentFactory } from './temp/componentBuilder';

// Configure the Grid in RD UI
setGridConfiguration({ gutterWidth: 24 });

// This is the main JSX entry point of the app invoked by the renderer (server or client rendering).
// By default the app's normal rendering is delegated to <RouteHandler> that handles the loading of JSS route data.

// support languages in the URL prefix
// e.g. /da-DK/path, or /en/path, or /path
const LANGUAGE_REG_EXP = /^\/?(([a-z]{2}-[A-Z]{2})|([a-z]{2}))(\/|$)/g;

export const parseRouteParams = (url: string) => {
  const language = url.match(LANGUAGE_REG_EXP);
  const route = url.replace(LANGUAGE_REG_EXP, '');

  return {
    route: route.startsWith('/') ? route : `/${route}`,
    language: language ? language[0].replace(/\//g, '') : undefined,
  };
};

interface JssRouteProps {
  isSSR: boolean;
}

const JssRoute: React.FC<JssRouteProps> = (props) => {
  const params = useParams();
  const url = params['*'] || '';

  return <RouteHandler {...parseRouteParams(url)} url={url} isSSR={props.isSSR} />;
};

// wrap the app with:
// ApolloProvider: provides an instance of Apollo GraphQL client to the app to make Connected GraphQL queries.
//    Not needed if not using connected GraphQL.
// SitecoreContext: provides component resolution and context services via withSitecoreContext
// Router: provides a basic routing setup that will resolve Sitecore item routes and allow for language URL prefixes.
interface AppRootProps {
  path: string;
  Router: any; // StaticRouter or BrowserRouter
  graphQLClient: ApolloClient; // Apollo Client v4: ApolloClient is no longer generic
  ssrState?: any;
}

class AppRoot extends React.Component<AppRootProps> {
  render() {
    const { path, Router, graphQLClient } = this.props;

    return (
      <CookiesProvider>
        <ThemeProvider theme={RddkTheme}>
          <ApolloProvider client={graphQLClient}>
            <SitecoreContext componentFactory={componentFactory} layoutData={this.props.ssrState}>
              <Router location={path} context={{}}>
                <Routes>
                  <Route path="*" element={<JssRoute isSSR={!!this.props.ssrState} />} />
                </Routes>
              </Router>
            </SitecoreContext>
          </ApolloProvider>
        </ThemeProvider>
      </CookiesProvider>
    );
  }
}

export default AppRoot;
